// #region Imports
import { DialogPaneWidth, useDialogContentWidth } from "_shared/_views/dialogs/dialog";
// #endregion

/**
 * The longest field value that has to stay whole: an email address.
 *
 * An address carries no space, no hyphen and no other break opportunity, so a track narrower than
 * the whole of it is a track that splits a word -- which is exactly what the field grid did at
 * 516px, turning `client.member.2@example.com` into `client.membe` / `r.2@example.` / `com` over
 * three lines. At the profile's body size that address comes to about 224px.
 */
export const PROFILE_EMAIL_WIDTH = 224;

/**
 * The longest field label: `Number Applications Drafted / Prosecuted`, about 290px at the label's
 * weight. `Collaborates With Other Individuals` is the next longest at about 255px.
 */
export const PROFILE_LABEL_WIDTH = 290;

/** What MUI's `DialogContent` keeps clear either side of whatever is drawn in it. */
export const PROFILE_DIALOG_CONTENT_PADDING = 48;

/** What the field flow keeps clear either side of itself where it has the width for it. */
export const PROFILE_CONTENT_MARGIN = 64;

/** What it keeps clear where it does not: the same margin the medium breakpoint already used. */
export const PROFILE_COMPACT_CONTENT_MARGIN = 16;

/** The gutter between the field flow's two columns. */
export const PROFILE_CONTENT_COLUMN_GAP = 8;

/**
 * How many tracks the comfortable arrangement cuts its container into.
 *
 * Two flow columns, each holding a field laid out as a label beside its value -- so four tracks of
 * text across, which is why a container that looks generous is not.
 */
export const PROFILE_COMFORTABLE_TRACKS = 4;

/**
 * The narrowest container the comfortable arrangement fits in, in pixels.
 *
 * `48 + 64 + 8 + 4 x 224 = 1016`. Below this one of its four tracks is narrower than an email
 * address, and the address is broken mid-word rather than wrapped -- there being nowhere in an
 * address to wrap. Arithmetic rather than taste, in the manner of `staffing-picker-stage`: the only
 * width here that content actually requires is the address, and everything else is chrome.
 */
export const PROFILE_FIELD_PAIR_MINIMUM =
  PROFILE_DIALOG_CONTENT_PADDING
  + PROFILE_CONTENT_MARGIN
  + PROFILE_CONTENT_COLUMN_GAP
  + PROFILE_COMFORTABLE_TRACKS * PROFILE_EMAIL_WIDTH;

/**
 * The narrowest container the compact arrangement still reads in, in pixels.
 *
 * `48 + 16 + 290 = 354`. One flow column of fields stacked label over value, so the widest thing
 * that has to fit across is the longest label. Stated so that a threshold proposed for the picker
 * can be checked against it rather than guessed at: the profile is not the constraint above this.
 */
export const PROFILE_COMPACT_MINIMUM =
  PROFILE_DIALOG_CONTENT_PADDING + PROFILE_COMPACT_CONTENT_MARGIN + PROFILE_LABEL_WIDTH;

/**
 * How the profile lays its fields out in the room it has been given.
 *
 * `comfortable` is two flow columns of label-beside-value, which is what the profile has always
 * done and what it still does wherever it has the page. `compact` is one flow column of
 * label-over-value, which is the same arrangement the profile already uses on a phone -- reused
 * here because the pane is narrow, rather than because the window is.
 */
export type ProfileFieldDensity = "comfortable" | "compact";

/**
 * Which arrangement the room the profile was given has space for.
 *
 * Only a stated pane width is ever measured against the threshold. Every other answer -- a profile
 * with no dialog around it, a dialog with no pane out, a column that is flexible or not drawn --
 * is `comfortable`, so the five surfaces that draw this profile at the width of the page are left
 * exactly as they were. The one caller that states a width is the staffing picker, which names
 * 516px for the profile's column at its widest arrangement.
 *
 * A container width rather than a window width, deliberately: it is the container that changed. The
 * same window holds the profile at 516px with the picker out and at the whole of the page with it
 * shut, so a media query cannot tell the two apart.
 */
export function profileFieldDensity(
  containerWidth: DialogPaneWidth | null | undefined,
): ProfileFieldDensity {
  if (typeof containerWidth !== "number") return "comfortable";
  return containerWidth < PROFILE_FIELD_PAIR_MINIMUM ? "compact" : "comfortable";
}

/** How the profile drawn here should lay its fields out, from the room the dialog gave it. */
export function useProfileFieldDensity(): ProfileFieldDensity {
  return profileFieldDensity(useDialogContentWidth());
}
